import * as React from "react";
import {
  CircularProgress,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Fab,
  Grid,
  Typography,
  Modal,
} from "@mui/material";
import { ERROR_MESSAGE, OK_MESSAGE } from "../../utils/Constants";
import { CheckCircleOutline } from "@mui/icons-material";
import { messageType } from "../../types/otherTypes";
import { useNavigate, useParams } from "react-router-dom";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { InvoiceType } from "../../types/entitiesModel";
import { AuthContext } from "../../context/AuthContext";


const ValidInvoice: React.FC = () => {
  const { token } = React.useContext(AuthContext) || {};
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [invoice, setInvoice] = React.useState<InvoiceType | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);

  const [message, setMessage] = React.useState<messageType>({
    message: "",
    type: OK_MESSAGE,
  });

  const handleBackView = () => {
    navigate("/admin/invoices");
  };

  const fetchInvoice = async (id: string) => {
    setLoading(true);
    try {
      let options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await fetch(`/api/invoices/${id}`, options);
      const data = await response.json();

      if (response.ok) {
        setInvoice(data.data);
        setError(null);
      } else {
        setError("Error obteniendo factura");
      }
    } catch (err) {
      setError("Error obteniendo factura");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (id) fetchInvoice(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (status: string) => {
    const requestBody = {
      status_id: status,
    };

    try {
      const response = await fetch(`/api/invoices/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const responseData = await response.json();

        setMessage({
          message: `Success: ${
            responseData.messages || "Data enviada satisfactoriamente"
          }`,
          type: OK_MESSAGE,
        });

        setTimeout(() => {
          navigate("/admin/invoices");
        }, 1000);
      } else {
        const errorData = await response.json();
        setMessage({
          message: `Error: ${errorData.message || "Algo ha ido mal"}`,
          type: ERROR_MESSAGE,
        });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setMessage({
          message: `Error: ${
            error.message || "Un error desconocido ha ocurrido"
          }`,
          type: ERROR_MESSAGE,
        });
      } else {
        setMessage({
          message: "Error: Error desconocido",
          type: ERROR_MESSAGE,
        });
      }
    }
  };

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  return (
    <>
      {!loading && invoice ? (
        <>
          {error && (
            <Alert
              sx={{
                mx: "auto",
                my: 2,
                width: "95%",
              }}
              severity="error"
            >
              {error}
            </Alert>
          )}
          <React.Fragment>
            <Grid container spacing={2}>
              {message.message !== "" &&
                (message.type === OK_MESSAGE ? (
                  <Alert
                    icon={<CheckCircleOutline fontSize="inherit" />}
                    severity="success"
                    sx={{
                      mx: "auto",
                      mb: 2,
                      width: "95%",
                    }}
                  >
                    {message.message}
                  </Alert>
                ) : (
                  <Alert
                    sx={{
                      m: "auto",
                      mb: 2,
                      width: "95%",
                    }}
                    severity="error"
                  >
                    {message.message}
                  </Alert>
                ))}
              <Fab
                color="primary"
                variant="extended"
                sx={{
                  mr: 1,
                  mb: {
                    xs: 1,
                    sm: 0,
                    lg: 0,
                  },
                  ml: 2,
                }}
                onClick={handleBackView}
              >
                <KeyboardDoubleArrowLeftIcon />
                <Typography
                  sx={{
                    ml: 1,
                    textTransform: "capitalize",
                  }}
                >
                  {"Tabla de facturas"}
                </Typography>
              </Fab>
              <Grid item lg={12} md={12} xs={12}>
                <div>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 0,
                    }}
                  >
                    <Box
                      sx={{
                        padding: "15px 30px",
                      }}
                      display="flex"
                      alignItems="center"
                    >
                      <Box flexGrow={1}>
                        <Typography
                          sx={{
                            fontSize: "18px",
                            fontWeight: "500",
                          }}
                        >
                          Validar factura {id}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider />
                    <CardContent
                      sx={{
                        padding: "30px",
                      }}
                    >
                      <form>
                        {/* Distribuir en tres columnas */}
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={4}>
                            <Card variant="outlined">
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                }}
                              >
                                Representante:
                              </Typography>
                              <Typography>{`${invoice.representative.name}`}</Typography>
                            </Card>

                            <Card variant="outlined">
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                }}
                              >
                                Estudiante:
                              </Typography>
                              <Typography>{`${invoice.course_student.student.name}`}</Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Card variant="outlined">
                              {" "}
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                }}
                              >
                                Curso:
                              </Typography>
                              <Typography>{`${invoice.course_student.course.group_name}`}</Typography>
                            </Card>

                            <Card variant="outlined">
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                }}
                              >
                                Método:
                              </Typography>
                              <Typography>{`${
                                invoice.payment
                                  ? invoice.payment.account_name
                                  : "-"
                              }`}</Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Card variant="outlined">
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                }}
                              >
                                Monto:
                              </Typography>
                              <Typography>{`${invoice.amount}`}</Typography>
                            </Card>

                            <Card variant="outlined">
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  mb: 1,
                                }}
                              >
                                Referencia:
                              </Typography>
                              <Typography>{`${invoice.reference}`}</Typography>
                            </Card>
                          </Grid>
                        </Grid>

                        <Grid
                          container
                          spacing={2}
                          sx={{
                            mt: 3,
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Grid item>
                            {/* <Button
                              onClick={() => {
                                handleSubmit("success");
                              }}
                              color="primary"
                              variant="contained"
                              sx={{ mr: 2 }}
                            >
                              Aprobar
                            </Button> */}
                          </Grid>
                          <Grid item>
                            <Button
                              onClick={handleOpenModal}
                              color="success"
                              variant="contained"
                            >
                              Confirmar
                            </Button>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          spacing={2}
                          sx={{
                            mt: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography sx={{ color: "#b9b9b9" }}>
                            Al confirmar debes estar seguro que el dinero se
                            hizo efectivo en la cuenta seleccionada
                          </Typography>
                        </Grid>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              </Grid>
            </Grid>

            {/* Modal de confirmación */}
            <Modal open={modalOpen} onClose={handleCloseModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 400,
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>
                  ¿Está seguro que desea confirmar la factura?
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: "#555" }}>
                  Recuerde que debe revisar la cuenta bancaria para este caso.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    onClick={handleCloseModal}
                    color="error"
                    variant="outlined"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => {
                      handleSubmit("success");
                      handleCloseModal();
                    }}
                    color="primary"
                    variant="contained"
                  >
                    Sí
                  </Button>
                </Box>
              </Box>
            </Modal>
          </React.Fragment>
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

export default ValidInvoice;
